/**
 * Single source of truth for URL route localization across ZETL CRM marketing website.
 * Complies with FE-005 (pure, decoupled helpers) and GEN-002 (English identifiers).
 */

export type SupportedLanguage = 'de' | 'en';

export const CANONICAL_SITE_URL = 'https://zetlcrm.com';

export interface RouteDefinition {
  id: string;
  sourceFile: string;
  paths: {
    de: string;
    en: string;
  };
  legacyAliases?: string[];
}

export const ROUTE_DEFINITIONS: RouteDefinition[] = [
  {
    id: 'home',
    sourceFile: 'index.html',
    paths: {
      de: '/',
      en: '/en/',
    },
    legacyAliases: ['/index.html', '/en/index.html'],
  },
  {
    id: 'contact',
    sourceFile: 'kontakt.html',
    paths: {
      de: '/kontakt',
      en: '/en/contact',
    },
    legacyAliases: ['/kontakt.html', '/en/kontakt', '/en/kontakt.html'],
  },
  {
    id: 'privacy',
    sourceFile: 'datenschutz.html',
    paths: {
      de: '/datenschutz',
      en: '/en/privacy',
    },
    legacyAliases: ['/datenschutz.html', '/en/datenschutz', '/en/datenschutz.html'],
  },
  {
    id: 'legalNotice',
    sourceFile: 'impressum.html',
    paths: {
      de: '/impressum',
      en: '/en/legal-notice',
    },
    legacyAliases: ['/impressum.html', '/en/impressum', '/en/impressum.html'],
  },
  {
    id: 'terms',
    sourceFile: 'agb.html',
    paths: {
      de: '/agb',
      en: '/en/terms',
    },
    legacyAliases: ['/agb.html', '/en/agb', '/en/agb.html'],
  },
  {
    id: 'dpa',
    sourceFile: 'avv.html',
    paths: {
      de: '/avv',
      en: '/en/dpa',
    },
    legacyAliases: ['/avv.html', '/en/avv', '/en/avv.html'],
  },
  {
    id: 'accessibility',
    sourceFile: 'barrierefreiheit.html',
    paths: {
      de: '/barrierefreiheit',
      en: '/en/accessibility',
    },
    legacyAliases: ['/barrierefreiheit.html', '/en/barrierefreiheit', '/en/barrierefreiheit.html'],
  },
  {
    id: 'subprocessors',
    sourceFile: 'subprocessors.html',
    paths: {
      de: '/unterauftragsverarbeiter',
      en: '/en/subprocessors',
    },
    legacyAliases: ['/subprocessors', '/subprocessors.html', '/en/subprocessors.html'],
  },
  {
    // The root certificate of the contract-signing CA (BE-023 point 9): every
    // signature protocol links here, so the path never changes.
    id: 'rootCertificate',
    sourceFile: 'stammzertifikat.html',
    paths: {
      de: '/signatur/stammzertifikat',
      en: '/en/signature/root-certificate',
    },
  },
  {
    id: 'customerManagement',
    sourceFile: 'customer-management.html',
    paths: {
      de: '/kundenverwaltung',
      en: '/en/customer-management',
    },
    legacyAliases: ['/customer-management', '/customer-management.html', '/en/customer-management.html'],
  },
  {
    id: 'finance',
    sourceFile: 'finance.html',
    paths: {
      de: '/finanzen',
      en: '/en/finance',
    },
    legacyAliases: ['/finance', '/finance.html', '/en/finance.html'],
  },
  {
    id: 'projects',
    sourceFile: 'projects.html',
    paths: {
      de: '/projekte',
      en: '/en/projects',
    },
    legacyAliases: ['/projects', '/projects.html', '/en/projects.html'],
  },
  {
    id: 'inventory',
    sourceFile: 'inventory-purchasing.html',
    paths: {
      de: '/warenwirtschaft',
      en: '/en/inventory-purchasing',
    },
    legacyAliases: [
      '/inventory-purchasing',
      '/inventory-purchasing.html',
      '/en/inventory-purchasing.html',
    ],
  },
  {
    id: 'advisor',
    sourceFile: 'advisor-portal.html',
    paths: {
      de: '/steuerberater-portal',
      en: '/en/advisor-portal',
    },
    legacyAliases: [
      '/steuerberater',
      '/steuerberater.html',
      '/tax-advisor',
      '/tax-advisor.html',
      '/advisor-portal',
      '/advisor-portal.html',
      '/steuerberater-portal.html',
      '/en/advisor-portal.html',
      '/en/tax-advisor',
      '/en/tax-advisor.html',
    ],
  },
];

/**
 * Normalizes a URL path by stripping trailing `.html` (except root), but preserving trailing slash for root.
 */
function normalizePath(rawPath: string): string {
  const [pathOnly] = rawPath.split(/[?#]/);
  let cleaned = pathOnly.replace(/\.html$/, '');
  if (cleaned.length > 1 && cleaned.endsWith('/')) {
    cleaned = cleaned.slice(0, -1);
  }
  return cleaned === '' ? '/' : cleaned;
}

/**
 * Finds a route definition matching a given URL pathname or source filename.
 */
export function findRouteByPath(urlPath: string): RouteDefinition | undefined {
  const norm = normalizePath(urlPath);
  for (const def of ROUTE_DEFINITIONS) {
    if (normalizePath(def.paths.de) === norm || normalizePath(def.paths.en) === norm) {
      return def;
    }
    if (def.legacyAliases && def.legacyAliases.some(alias => normalizePath(alias) === norm)) {
      return def;
    }
  }
  return undefined;
}

/**
 * Finds a route definition by its source HTML file name.
 */
export function findRouteBySource(sourceFile: string): RouteDefinition | undefined {
  const base = sourceFile.replace(/^.*[\\/]/, '');
  return ROUTE_DEFINITIONS.find(def => def.sourceFile === base);
}

/**
 * Resolves a URL to its localized target equivalent in the given target language.
 * Preserves query params and hash anchors.
 */
export function resolveLocalizedUrl(rawUrl: string, targetLang: SupportedLanguage): string {
  // Extract path, search, and hash
  const match = rawUrl.match(/^([^?#]*)([?][^#]*)?(#.*)?$/);
  const pathPart = match ? match[1] : rawUrl;
  const searchPart = match && match[2] ? match[2] : '';
  const hashPart = match && match[3] ? match[3] : '';

  const route = findRouteByPath(pathPart);
  if (route) {
    return `${route.paths[targetLang]}${searchPart}${hashPart}`;
  }

  // Fallback for unmapped paths:
  const norm = normalizePath(pathPart);
  if (targetLang === 'en') {
    if (norm.startsWith('/en/') || norm === '/en') {
      return `${norm}${searchPart}${hashPart}`;
    }
    const suffix = norm === '/' ? '/' : norm;
    return `/en${suffix}${searchPart}${hashPart}`;
  } else {
    // Switching to de
    if (norm.startsWith('/en/')) {
      const deSuffix = norm.slice(3) || '/';
      return `${deSuffix}${searchPart}${hashPart}`;
    }
    if (norm === '/en') {
      return `/${searchPart}${hashPart}`;
    }
    return `${norm}${searchPart}${hashPart}`;
  }
}

/**
 * Returns the absolute canonical URL for a given template file and language.
 */
export function getCanonicalUrl(sourceFile: string, lang: SupportedLanguage): string {
  const route = findRouteBySource(sourceFile);
  const relativePath = route ? route.paths[lang] : (lang === 'en' ? `/en/${sourceFile}` : `/${sourceFile}`);
  if (relativePath === '/') {
    return `${CANONICAL_SITE_URL}/`;
  }
  return `${CANONICAL_SITE_URL}${relativePath}`;
}

/**
 * Returns the alternate hreflang dictionary for a given template file.
 */
export function getHreflangPairs(sourceFile: string): { de: string; en: string; 'x-default': string } {
  const deCanonical = getCanonicalUrl(sourceFile, 'de');
  const enCanonical = getCanonicalUrl(sourceFile, 'en');
  return {
    de: deCanonical,
    en: enCanonical,
    'x-default': deCanonical,
  };
}

/**
 * Returns the relative German output file name (e.g. `kundenverwaltung.html` for `customer-management.html`).
 */
export function getGermanOutputFilename(sourceFile: string): string {
  const route = findRouteBySource(sourceFile);
  if (!route) return sourceFile;
  if (route.paths.de === '/' || route.paths.de === '') {
    return 'index.html';
  }
  const slug = route.paths.de.replace(/^\//, '');
  return `${slug}.html`;
}

/**
 * Returns the relative English output file name (e.g. `contact.html` for `kontakt.html`).
 */
export function getEnglishOutputFilename(sourceFile: string): string {
  const route = findRouteBySource(sourceFile);
  if (!route) return sourceFile;
  if (route.paths.en === '/en/' || route.paths.en === '/en') {
    return 'index.html';
  }
  const slug = route.paths.en.replace(/^\/en\//, '');
  return `${slug}.html`;
}
